<template>
    <div class="site-build-batch container">
        <div v-if="isBuilding" class="row">
            <div class="col">
                <small class="text-muted">
                    <span v-if="refreshedAt">Última atualização: {{ refreshedAt.toLocaleDateString('pt-BR') }} às {{ refreshedAt.toLocaleTimeString('pt-BR') }}</span>
                    <span v-else>Completo: {{ finishedBuildCount }} / {{ totalBuildCount }}</span>
                </small>
                <div class="progress">
                    <div class="progress-bar" role="progressbar" :style="finishedBuildProgressStyle" :aria-valuenow="finishedBuildCount">{{ finishedPercentage }}%</div>
                    <div class="progress-bar progress-bar-striped" role="progressbar" :style="pendingBuildProgressStyle" :aria-valuenow="pendingBuildCount"></div>
                </div>
            </div>
        </div>
        <div v-else class="row">
            <div class="col-md-6">
                <div class="form-check form-check-inline">
                    <input type="checkbox" name="all-sites" id="all-sites" class="form-check-input" :checked="allSitesAreSelected" @change="handleSelectAllChange">
                    <label class="form-check-label" for="all-sites">Marcar todos</label>
                </div>
            </div>
            <div class="col-md-2 offset-md-4 text-end">
                <button class="btn btn-primary btn-block" :disabled="selectedSites.length === 0" @click="handleBuild">Build</button>
            </div>
        </div>
        <div class="row mt-3 align-items-stretch">
            <div v-for="site in visibleSites" :key="site.id" class="col-md-4 mb-4">
                <label :class="['site-opt', 'card', isSelected(site) && !isBuilding ? 'selected' : '']">
                    <div class="row">
                        <div class="col-md-4">
                            <img :src="site.logoUrl" class="card-img" :alt="site.name">
                        </div>
                        <div class="col-md-8">
                            <div class="card-body">
                                <input v-if="!isBuilding" type="checkbox" :value="site.id" v-model="selectedSites" class="d-none">
                                <h6 class="card-title">{{ site.name }}</h6>
                                <em v-if="isBuilding" :class="['card-text', 'build-status', `status-${getBuildStatus(site)}`]">{{ getBuildStatusPretty(site) }}</em>
                            </div>
                        </div>
                    </div>
                </label>
            </div>
        </div>
    </div>
</template>

<script>
import { requestJson } from '../helpers';

export default {
    props: {
        buildableSitesUrl: String,
        dashboardUrl: String,
    },
    data(){
        return {
            buildableSites: [],
            selectedSites: [],
            isBuilding: false,
            buildIndex: {}, // { [siteId]: string }
            currentSiteIndex: null,
            intervalRef: null,
            refreshedAt: null,
        };
    },
    computed: {
        sitesToBuild(){
            return this.buildableSites.filter(site => this.selectedSites.includes(site.id));
        },
        visibleSites(){
            return this.isBuilding ? this.sitesToBuild : this.buildableSites;
        },
        allSitesAreSelected(){
            return this.selectedSites.length > 0 && this.selectedSites.length === this.buildableSites.length;
        },
        finishedBuildCount(){
            return this.countBuilds(build => build?.isFinished);
        },
        pendingBuildCount(){
            return this.countBuilds(build => build && !build.isFinished);
        },
        totalBuildCount(){
            return this.sitesToBuild.length;
        },
        finishedBuildProgressStyle(){
            return this.makeProgressStyle(this.finishedBuildCount);
        },
        pendingBuildProgressStyle(){
            return this.makeProgressStyle(this.pendingBuildCount);
        },
        finishedPercentage(){
            return this.totalBuildCount > 0 ? Math.round(this.finishedBuildCount / this.totalBuildCount * 100) : 0;
        },
    },
    methods: {
        countBuilds(predicate){
            const filtered = this.sitesToBuild.filter(site => {
                const build = this.getBuildLog(site);
                return predicate(build);
            });
            return filtered.length;
        },
        makeProgressStyle(count){
            const percentage = this.totalBuildCount > 0 ? Math.round(count / this.totalBuildCount * 100) : 0;
            return `width: ${percentage}%`;
        },
        async loadBuildableSites(){
            this.buildableSites = await requestJson(this.buildableSitesUrl);
        },
        isSelected(site){
            return this.selectedSites.includes(site.id);
        },
        handleSelectAllChange({ target: { checked } }){
            this.selectedSites = checked ? this.buildableSites.map(site => site.id) : [];
        },
        handleBuild(){
            if (this.selectedSites.length === 0) {
                alert('Selecione pelo menos um site para buildar.');
                return;
            }

            this.isBuilding = true;
            this.currentSiteIndex = 0;

            this.intervalRef = setInterval(() => {
                this.refreshBuildLogs();
                this.refreshedAt = new Date();
            }, 3000);
            this.refreshBuildLogs();
        },
        async refreshBuildLogs(){
            // If we have already processed all sites, stop the interval and return
            if (this.currentSiteIndex >= this.sitesToBuild.length) {
                clearInterval(this.intervalRef);
                this.intervalRef = null;
                return;
            }

            // Get the current site and its build status
            const currSite = this.sitesToBuild[this.currentSiteIndex];
            const build = this.getBuildLog(currSite);

            // If there's no build log for the current site, trigger a new build and return
            if (!build) {
                const freshBuild = await requestJson(currSite.createSiteBuildUrl, null, 'POST');

                if (freshBuild.error) {
                    console.error(`Failed to trigger build for site ${currSite.name}:`, freshBuild.error);

                    this.setBuildLog(currSite, {
                        status: 'failed',
                        isFinished: true,
                    });

                    this.currentSiteIndex++;
                } else {
                    this.setBuildLog(currSite, freshBuild);
                }

                return;
            }

            // Retrieve the updated build status from the server
            const updatedBuild = await requestJson(build.getBuildUrl);
            this.setBuildLog(currSite, updatedBuild);

            // If the build is finished, move to the next site and refresh logs again
            if (updatedBuild.isFinished) {
                this.currentSiteIndex++;
                this.refreshBuildLogs();
            }
        },
        getBuildLog(site){
            return this.buildIndex[site.id] ?? null;
        },
        setBuildLog(site, build){
            this.$set(this.buildIndex, site.id, build);
        },
        getBuildStatus(site){
            return this.getBuildLog(site)?.status ?? 'pending';
        },
        getBuildStatusPretty(site){
            switch(this.getBuildStatus(site)){
                case 'pending':
                    return 'Pendente...';
                case 'queued':
                    return 'Na fila...';
                case 'started':
                    return 'Em andamento...';
                case 'succeeded':
                    return 'Sucesso!';
                case 'failed':
                    return 'Falhou';
                case 'cancelled':
                    return 'Cancelado';
                default:
                    return `Status desconhecido: ${log.status}`;
            }
        },
        handleBeforeUnload(event){
            if (!this.isBuilding) {
                return;
            }

            const finishedAll = this.finishedBuildCount === this.totalBuildCount;
            if (!finishedAll && !confirm('Há builds em andamento. Tem certeza que deseja sair?')) {
                event.preventDefault();
                event.returnValue = '';
            }
        },
    },
    mounted(){
        this.loadBuildableSites();

        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },
}
</script>

<style lang="scss" scoped>
.site-opt {
    min-height: 100%;

    &.selected {
        background-color: royalblue;
        color: white;
    }
}

.build-status {
    font-size: 0.9em;
    font-style: normal;

    &.status-pending {
        color: gray;
    }
    &.status-queued {
        color: orange;
    }
    &.status-started {
        color: blue;
    }
    &.status-succeeded {
        color: green;
    }
    &.status-failed {
        color: red;
    }
    &.status-cancelled {
        color: darkred;
    }
}
</style>
